/**
 * Nethinking SSO helpers.
 * The backend returns an opaque AES-256-GCM token (Base64URL).
 * The browser must never encrypt, decrypt, inspect, or re-encode that token.
 */

export const NETHINKING_ALLOWED_HOSTNAME = "nethinkingclub.com";

export function getNethinkingBaseUrl() {
  const raw = process.env.VUE_APP_NETHINKING_BASE_URL || "";
  return String(raw).trim().replace(/\/?$/, "/");
}

/**
 * Validates redirectUrl before navigation.
 * - HTTPS only
 * - Hostname must be exactly nethinkingclub.com
 * - No embedded credentials
 * - Path must stay under the configured base path (e.g. /espic/)
 *
 * @param {string} redirectUrl
 * @param {string} [baseUrl]
 * @returns {boolean}
 */
export function isAllowedNethinkingRedirectUrl(redirectUrl, baseUrl = getNethinkingBaseUrl()) {
  if (!redirectUrl) return false;
  try {
    const target = new URL(String(redirectUrl));

    if (target.protocol !== "https:") return false;
    if (target.username || target.password) return false;
    if (target.hostname !== NETHINKING_ALLOWED_HOSTNAME) return false;

    if (baseUrl) {
      const base = new URL(baseUrl);
      if (base.hostname !== NETHINKING_ALLOWED_HOSTNAME) return false;
      if (!target.pathname.startsWith(base.pathname)) return false;
    }

    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Prefer backend redirectUrl when safe.
 * Fallback: configured base + opaque token (no re-encoding).
 *
 * @param {{ token?: string, redirectUrl?: string }} payload
 * @param {string} [baseUrl]
 * @returns {string|null}
 */
export function resolveNethinkingRedirectUrl(payload, baseUrl = getNethinkingBaseUrl()) {
  if (!payload || typeof payload !== "object") return null;

  const redirectUrl = payload.redirectUrl || payload.RedirectUrl;
  if (redirectUrl && isAllowedNethinkingRedirectUrl(redirectUrl, baseUrl)) {
    // Use backend URL as-is; do not alter the opaque token segment.
    return String(redirectUrl);
  }

  const token = payload.token || payload.Token;
  if (!token || !baseUrl) return null;

  // Token is already Base64URL — never encodeURIComponent / modify it.
  const built = `${baseUrl}${String(token)}`;
  return isAllowedNethinkingRedirectUrl(built, baseUrl) ? built : null;
}

function clearOpener(tab) {
  if (!tab) return;
  try {
    tab.opener = null;
  } catch (e) {
    // Some browsers may block writing opener; ignore.
  }
}

/**
 * User-gesture SSO flow:
 * 1) open blank tab immediately
 * 2) POST /sso-token
 * 3) validate redirectUrl and navigate
 * 4) close tab on failure
 *
 * Does not persist or log token / redirectUrl.
 *
 * @param {{
 *   createSsoToken: () => Promise<{ data: { token?: string, redirectUrl?: string, expiresIn?: number } }>,
 *   openBlankTab?: () => Window|null,
 *   assignTabUrl?: (tab: Window, url: string) => void,
 *   closeTab?: (tab: Window|null) => void,
 * }} deps
 * @returns {Promise<{ expiresIn: number|null }>}
 */
export async function startNethinkingSso({
  createSsoToken,
  openBlankTab = () => window.open("about:blank", "_blank"),
  assignTabUrl = (tab, url) => {
    clearOpener(tab);
    tab.location.replace(url);
  },
  closeTab = (tab) => {
    if (tab && !tab.closed) tab.close();
  },
}) {
  if (typeof createSsoToken !== "function") {
    throw new Error("createSsoToken is required");
  }

  const tab = openBlankTab();
  clearOpener(tab);

  try {
    const response = await createSsoToken();
    const data = response && response.data ? response.data : response;
    const redirectUrl = resolveNethinkingRedirectUrl(data);

    if (!redirectUrl) {
      closeTab(tab);
      const error = new Error("INVALID_REDIRECT");
      error.code = "INVALID_REDIRECT";
      throw error;
    }

    if (tab && !tab.closed) {
      assignTabUrl(tab, redirectUrl);
    } else {
      const fallback = window.open(redirectUrl, "_blank", "noopener,noreferrer");
      clearOpener(fallback);
    }

    const expiresIn =
      data.expiresIn != null ? data.expiresIn : data.ExpiresIn != null ? data.ExpiresIn : null;

    // Never return token; expiresIn is non-sensitive metadata only.
    return { expiresIn };
  } catch (error) {
    closeTab(tab);
    throw error;
  }
}
